"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.galileo = void 0;
/* eslint-disable class-methods-use-this */
const lru_cache_1 = __importDefault(require("lru-cache"));
const object_hash_1 = require("object-hash");
const helpers_1 = require("../helpers");
const service_1 = require("../service");
class Galileo {
    constructor() {
        this.cache = new lru_cache_1.default({ maxAge: 5 * 60 * 1000 });
        this.variableRegExp = new RegExp('^([a-z][a-z0-9_]+)\\/([a-z][a-z0-9_]+)$');
        this.commonTags = {
            platform: 'web',
            env: 'prd',
            visitor: (0, helpers_1.getVisitor)(),
        };
    }
    setLocation() {
        return __awaiter(this, void 0, void 0, function* () {
            // if location is set, SDK shouldn't ask for location any more
            if (this.commonTags.location) {
                return;
            }
            try {
                const position = yield new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject));
                this.setTag('location', `${position.coords.latitude},${position.coords.longitude}`);
            }
            catch (error) {
                console.warn("Galileo SDK: Location wasn't set. User didn't allow geolocation access.");
            }
        });
    }
    getBool(variable, tags, defaultValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.getVariableValue(variable, tags, defaultValue);
            if (this.doTypesMatch(variable, typeof value, 'boolean'))
                return value;
            return defaultValue;
        });
    }
    getNumber(variable, tags, defaultValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.getVariableValue(variable, tags, defaultValue);
            if (this.doTypesMatch(variable, typeof value, 'number'))
                return value;
            return defaultValue;
        });
    }
    getString(variable, tags, defaultValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.getVariableValue(variable, tags, defaultValue);
            if (this.doTypesMatch(variable, typeof value, 'string'))
                return value;
            return defaultValue;
        });
    }
    getObject(variable, tags, defaultValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const value = yield this.getVariableValue(variable, tags, JSON.stringify(defaultValue));
            try {
                const obj = JSON.parse(value);
                // needed to handle cases like: JSON.parse('true'), JSON.parse('1'),...
                if (this.doTypesMatch(variable, typeof obj, 'object'))
                    return obj;
                return defaultValue;
            }
            catch (error) {
                return defaultValue;
            }
        });
    }
    getVariableValue(variable, tags, defaultValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const lowerVariable = variable.toLowerCase();
            // validate variable name, if is not valid, return default value
            if (!this.variableRegExp.test(lowerVariable)) {
                console.warn(`Galileo SDK: Variable name '${lowerVariable}' doesn't match the regex '${this.variableRegExp.source}'.`);
                return defaultValue;
            }
            const key = `${lowerVariable}-${(0, object_hash_1.MD5)(Object.assign(Object.assign({}, this.commonTags), tags))}`;
            const cachedValue = this.cache.get(key);
            if (cachedValue !== undefined) {
                return cachedValue;
            }
            const results = yield (0, service_1.resolveVariables)([{ variable: lowerVariable, value: defaultValue }], Object.assign(Object.assign({}, this.commonTags), tags));
            this.cache.set(key, results[0].value);
            return results[0].value;
        });
    }
    getMany(variablesWithDefaults, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const batchVariables = [];
            const lowerDefaults = {};
            const results = {};
            const tagsHash = (0, object_hash_1.MD5)(Object.assign(Object.assign({}, this.commonTags), tags));
            Object.keys(variablesWithDefaults).forEach((variableName) => __awaiter(this, void 0, void 0, function* () {
                const lowerCaseVariable = variableName.toLowerCase();
                lowerDefaults[lowerCaseVariable] = variablesWithDefaults[variableName];
                if (!this.variableRegExp.test(lowerCaseVariable)) {
                    console.warn(`Galileo SDK: Variable name '${lowerCaseVariable}' doesn't match the regex '${this.variableRegExp.source}'.`);
                }
                // all variables check cached values
                const key = `${lowerCaseVariable}-${tagsHash}`;
                const cachedValue = this.cache.get(key);
                if (cachedValue === undefined) {
                    batchVariables.push({
                        variable: lowerCaseVariable,
                        value: variablesWithDefaults[variableName],
                    });
                }
                else {
                    results[lowerCaseVariable] = cachedValue;
                }
            }));
            // query galileo for missing variables not in cache
            const resolvedVariables = yield (0, service_1.resolveVariables)(batchVariables, Object.assign(Object.assign({}, this.commonTags), tags));
            resolvedVariables.forEach((variableResult) => {
                // set in cache
                const key = `${variableResult.variable}-${tagsHash}`;
                this.cache.set(key, variableResult.value);
                // add to results dict
                results[variableResult.variable] = this.parseAsType(variableResult.value, typeof lowerDefaults[variableResult.variable]);
            });
            return results;
        });
    }
    parseAsType(value, type) {
        switch (type) {
            case 'number':
                return value;
            case 'boolean':
                return value;
            case 'string':
                return value.toString();
            default:
                return value;
        }
    }
    isValidTagValue(tag, value, regex) {
        if (new RegExp(regex).test(value)) {
            return true;
        }
        console.warn(`Galileo SDK: Tag '${tag}' wasn't set. Value '${value}' doesn't match the regex '${regex}'.`);
        return false;
    }
    doTypesMatch(variable, actual, expected) {
        if (actual === expected) {
            return true;
        }
        console.warn(`Galileo SDK: Actual and expected variable type for variable '${variable}' doesn't match (actual: '${actual}', expected '${expected}').`);
        return false;
    }
    setTTL(value) {
        this.cache.maxAge = value;
    }
    setApiURL(value) {
        service_1.axiosInstance.defaults.baseURL = value;
    }
    setTimeout(value) {
        service_1.axiosInstance.defaults.timeout = value;
    }
    setTag(key, value) {
        this.commonTags = Object.assign(Object.assign({}, this.commonTags), { [key]: value });
    }
    getTag(key) {
        return this.commonTags[key];
    }
    setEnvironment(value) {
        this.setTag('env', value);
    }
    setAppName(value) {
        if (this.isValidTagValue('app_name', value, '^[a-z_]+$'))
            this.setTag('app_name', value);
    }
    setAppVersion(value) {
        this.setTag('app_version', value);
    }
    setUserID(value) {
        this.setTag('user', value);
    }
    setCountryCode(value) {
        if (this.isValidTagValue('country_code', value, '^[A-Z][A-Z]$'))
            this.setTag('country_code', value);
    }
    setSession(value) {
        this.setTag('session', value);
    }
}
exports.galileo = new Galileo();
